<script>
import { mapGetters } from 'vuex';
import { shouldBeUrl } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import Avatar from 'next/avatar/Avatar.vue';
import SettingsSection from '../../../../components/SettingsSection.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import FacebookReauthorize from './facebook/Reauthorize.vue';
import InstagramReauthorize from './channels/instagram/Reauthorize.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import MicrosoftReauthorize from './channels/microsoft/Reauthorize.vue';
import GoogleReauthorize from './channels/google/Reauthorize.vue';
import WhatsappReauthorize from './channels/whatsapp/Reauthorize.vue';
import PreChatFormSettings from './PreChatForm/Settings.vue';
import TicketFormSettings from './TicketForm/Settings.vue';
import PostChatFormSettings from './PostChatForm/Settings.vue';
import WelcomeScreen from './WelcomeScreen.vue';
import LanguageSettings from './LanguageSettings.vue';
import AvailabilitySettings from './AvailabilitySettings.vue';
import InactivityTimeouts from './InactivityTimeouts.vue';
import EyeCatcherSettings from './EyeCatcherSettings.vue';
import BubbleLauncherSettings from './BubbleLauncherSettings.vue';
import PoweredBySettings from './PoweredBySettings.vue';
import GreetingCardSettings from './GreetingCardSettings.vue';
import WeeklyAvailability from './components/WeeklyAvailability.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import GreetingConfigEditor from 'dashboard/components/widgets/GreetingConfigEditor.vue';
import ConfigurationPage from './settingsPage/ConfigurationPage.vue';
import CustomerSatisfactionPage from './settingsPage/CustomerSatisfactionPage.vue';
import CollaboratorsPage from './settingsPage/CollaboratorsPage.vue';
import WidgetBuilder from './WidgetBuilder.vue';
import BotConfiguration from './components/BotConfiguration.vue';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import SenderNameExamplePreview from './components/SenderNameExamplePreview.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { WIDGET_BUILDER_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

export default {
  components: {
    BotConfiguration,
    CollaboratorsPage,
    ConfigurationPage,
    CustomerSatisfactionPage,
    FacebookReauthorize,
    GreetingsEditor,
    GreetingConfigEditor,
    PreChatFormSettings,
    TicketFormSettings,
    PostChatFormSettings,
    WelcomeScreen,
    LanguageSettings,
    AvailabilitySettings,
    InactivityTimeouts,
    EyeCatcherSettings,
    BubbleLauncherSettings,
    PoweredBySettings,
    GreetingCardSettings,
    SettingsSection,
    WeeklyAvailability,
    WidgetBuilder,
    SenderNameExamplePreview,
    MicrosoftReauthorize,
    GoogleReauthorize,
    NextButton,
    InstagramReauthorize,
    WhatsappReauthorize,
    DuplicateInboxBanner,
    Editor,
    Avatar,
    ChannelIcon,
  },
  mixins: [inboxMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: '',
      greetingEnabled: true,
      greetingMessage: '',
      greetingConfig: { messages: [], buttons_enabled: false, buttons: [] },
      emailCollectEnabled: false,
      senderNameType: 'friendly',
      businessName: '',
      locktoSingleConversation: false,
      allowMessagesAfterResolved: true,
      continuityViaEmail: true,
      selectedInboxName: '',
      channelWebsiteUrl: '',
      webhookUrl: '',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      selectedFeatureFlags: [],
      replyTime: '',
      ailabAiOverrideMode: 'inherit',
      ailabAiOverrideReason: '',
      ailabAiOverrideSetAt: '',
      ailabAiOverrideSetByUserId: '',
      selectedTabIndex: 0,
      selectedPortalSlug: '',
      showBusinessNameInput: false,
      welcomeTaglineEditorMenuOptions: WIDGET_BUILDER_EDITOR_MENU_OPTIONS,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'inboxes/getUIFlags',
      portals: 'portals/allPortals',
    }),
    selectedTabKey() {
      // Route query takes precedence — keeps sidebar and content in sync
      const queryTab = this.$route.query?.tab;
      if (queryTab) {
        const exists = this.tabs.some(t => t.key === queryTab);
        if (exists) return queryTab;
      }
      return this.tabs[this.selectedTabIndex]?.key;
    },
    isAilabAiOverrideInherit() {
      return (this.ailabAiOverrideMode || 'inherit') === 'inherit';
    },
    nextAilabAiOverrideMode() {
      const current = (this.ailabAiOverrideMode || 'inherit').toString();
      if (current === 'suggest') return 'auto_suggest';
      if (current === 'auto_suggest') return 'inherit';
      return 'inherit';
    },
    canAdvanceAilabAiOverride() {
      const current = (this.ailabAiOverrideMode || 'inherit').toString();
      return current === 'suggest' || current === 'auto_suggest';
    },
    nextAilabAiOverrideModeLabel() {
      const nextMode = this.nextAilabAiOverrideMode;
      if (nextMode === 'suggest')
        return this.$t(
          'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.OPTIONS.SUGGEST'
        );
      if (nextMode === 'auto_suggest')
        return this.$t(
          'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.OPTIONS.AUTO_SUGGEST'
        );
      return this.$t(
        'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.OPTIONS.INHERIT'
      );
    },
    shouldShowWhatsAppConfiguration() {
      return this.isAWhatsAppCloudChannel;
    },
    whatsAppAPIProviderName() {
      if (this.isAWhatsAppCloudChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD');
      }
      if (this.is360DialogWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.360_DIALOG');
      }
      if (this.isATwilioWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO');
      }
      return '';
    },
    tabs() {
      let visibleToAllChannelTabs = [
        {
          key: 'inbox_settings',
          name: this.$t('INBOX_MGMT.TABS.SETTINGS'),
        },
        {
          key: 'collaborators',
          name: this.$t('INBOX_MGMT.TABS.COLLABORATORS'),
        },
      ];

      if (!this.isAVoiceChannel) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'businesshours',
            name: this.$t('INBOX_MGMT.TABS.BUSINESS_HOURS'),
          },
          {
            key: 'csat',
            name: this.$t('INBOX_MGMT.TABS.CSAT'),
          },
        ];
      }

      if (this.isAWebWidgetInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'preChatForm',
            name: this.$t('INBOX_MGMT.TABS.PRE_CHAT_FORM'),
          },
          {
            key: 'ticketForm',
            name: 'Ticket form',
          },
          {
            key: 'postChatForm',
            name: 'Post-chat form',
          },
          {
            key: 'widgetBuilder',
            name: this.$t('INBOX_MGMT.TABS.WIDGET_BUILDER'),
          },
          {
            key: 'eyeCatcher',
            name: 'Eye-catcher',
          },
          {
            key: 'bubbleLauncher',
            name: 'Bubble launcher',
          },
          {
            key: 'poweredBy',
            name: 'Powered by',
          },
          {
            key: 'welcomeScreen',
            name: 'Welcome screen',
          },
          {
            key: 'language',
            name: 'Language',
          },
          {
            key: 'greetingCard',
            name: 'Greeting card',
          },
          {
            key: 'availability',
            name: 'Availability',
          },
          {
            key: 'inactivityTimeouts',
            name: 'Inactivity timeouts',
          },
        ];
      }

      if (
        this.isATwilioChannel ||
        this.isALineChannel ||
        this.isAPIInbox ||
        this.isAVoiceChannel ||
        (this.isAnEmailChannel && !this.inbox.provider) ||
        this.shouldShowWhatsAppConfiguration ||
        this.isAWebWidgetInbox ||
        (this.isAWhatsAppChannel && this.inbox.provider === 'green_api')
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'configuration',
            name: this.$t('INBOX_MGMT.TABS.CONFIGURATION'),
          },
        ];
      }

      if (
        this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.AGENT_BOTS)
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'botConfiguration',
            name: this.$t('INBOX_MGMT.TABS.BOT_CONFIGURATION'),
          },
        ];
      }
      return visibleToAllChannelTabs;
    },
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentInboxId);
    },
    inboxIcon() {
      const { medium, channel_type: type } = this.inbox;
      return getInboxIconByType(type, medium);
    },
    inboxName() {
      if (this.isATwilioSMSChannel || this.isATwilioWhatsAppChannel) {
        return `${this.inbox.name} (${
          this.inbox.messaging_service_sid || this.inbox.phone_number
        })`;
      }
      if (this.isAWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.phone_number})`;
      }
      if (this.isAnEmailChannel) {
        return `${this.inbox.name} (${this.inbox.email})`;
      }
      return this.inbox.name;
    },
    canLocktoSingleConversation() {
      return (
        this.isASmsInbox ||
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAPIInbox
      );
    },
    inboxNameLabel() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL');
    },
    inboxNamePlaceHolder() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER');
    },
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
    instagramUnauthorized() {
      return this.isAnInstagramChannel && this.inbox.reauthorization_required;
    },
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return this.inbox.channel_type === INBOX_TYPES.FB && instagramInbox;
    },
    microsoftUnauthorized() {
      return this.isAMicrosoftInbox && this.inbox.reauthorization_required;
    },
    facebookUnauthorized() {
      return this.isAFacebookInbox && this.inbox.reauthorization_required;
    },
    googleUnauthorized() {
      const isLegacyInbox = ['imap.gmail.com', 'imap.google.com'].includes(
        this.inbox.imap_address
      );

      return (
        (this.isAGoogleInbox || isLegacyInbox) &&
        this.inbox.reauthorization_required
      );
    },
    whatsappUnauthorized() {
      return (
        this.isAWhatsAppChannel &&
        this.inbox.provider === 'whatsapp_cloud' &&
        this.inbox.provider_config?.source === 'embedded_signup' &&
        this.inbox.reauthorization_required
      );
    },
  },
  watch: {
    $route(to, from) {
      if (to.name === 'settings_inbox_show') {
        // Different inbox — full reload
        if (to.params.inboxId !== from.params.inboxId) {
          this.fetchInboxSettings();
          return;
        }
        // Same inbox, only tab changed (sidebar clicked) — just sync selectedTabIndex
        const queryTab = to.query?.tab;
        if (queryTab) {
          const idx = this.tabs.findIndex(t => t.key === queryTab);
          if (idx !== -1 && idx !== this.selectedTabIndex) {
            this.onTabChange(idx);
          }
        }
      }
    },
  },
  mounted() {
    this.fetchInboxSettings();
    this.fetchPortals();
  },
  methods: {
    fetchPortals() {
      this.$store.dispatch('portals/index');
    },
    goBack() {
      this.$router.push({ name: 'settings_inbox_list' });
    },
    hasTabKey(key) {
      return this.tabs.some(t => t.key === key);
    },
    selectTabKey(key, extraQuery = {}) {
      const idx = this.tabs.findIndex(t => t.key === key);
      if (idx === -1) return;
      this.onTabChange(idx);
      const nextQuery = { ...this.$route.query, tab: key, ...extraQuery };
      if (
        key !== 'language' &&
        !Object.prototype.hasOwnProperty.call(extraQuery, 'languageSection')
      ) {
        delete nextQuery.languageSection;
      }
      // Keep the URL (and sidebar active state) in sync
      this.$router
        .replace({
          query: nextQuery,
        })
        .catch(() => {});
    },
    goToLanguageSection(section) {
      this.selectTabKey('language', { languageSection: section });
    },
    handleFeatureFlag(e) {
      this.selectedFeatureFlags = this.toggleInput(
        this.selectedFeatureFlags,
        e.target.value
      );
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    refreshAvatarUrlOnTabChange(index) {
      if (
        this.inbox &&
        ['inbox_settings', 'widgetBuilder'].includes(this.tabs[index].key)
      )
        this.avatarUrl = this.inbox.avatar_url;
    },
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
      this.refreshAvatarUrlOnTabChange(selectedTabIndex);
    },
    fetchInboxSettings() {
      // Respect the route query tab if already set (e.g. navigating via sidebar)
      const queryTab = this.$route.query?.tab;
      if (queryTab) {
        const idx = this.tabs.findIndex(t => t.key === queryTab);
        this.selectedTabIndex = idx !== -1 ? idx : 0;
      } else {
        this.selectedTabIndex = 0;
      }
      this.selectedAgents = [];
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      this.$store.dispatch('labels/get');
      this.$store.dispatch('inboxes/get').then(() => {
        this.avatarUrl = this.inbox.avatar_url;
        this.selectedInboxName = this.inbox.name;
        this.webhookUrl = this.inbox.webhook_url;
        this.greetingEnabled = this.inbox.greeting_enabled || false;
        this.greetingMessage = this.inbox.greeting_message || '';
        this.greetingConfig = this.inbox.greeting_config || {
          messages: [],
          buttons_enabled: false,
          buttons: [],
        };
        this.emailCollectEnabled = this.inbox.enable_email_collect;
        this.senderNameType = this.inbox.sender_name_type;
        this.businessName = this.inbox.business_name;
        this.allowMessagesAfterResolved =
          this.inbox.allow_messages_after_resolved;
        this.continuityViaEmail = this.inbox.continuity_via_email;
        this.channelWebsiteUrl = this.inbox.website_url;
        this.channelWelcomeTitle = this.inbox.welcome_title;
        this.channelWelcomeTagline = this.inbox.welcome_tagline || '';
        this.selectedFeatureFlags = this.inbox.selected_feature_flags || [];
        this.replyTime = this.inbox.reply_time;
        this.locktoSingleConversation = this.inbox.lock_to_single_conversation;

        const settings = this.inbox.captain_ai_settings || {};
        this.ailabAiOverrideMode =
          settings.captain_ai_override_mode || 'inherit';
        this.ailabAiOverrideReason =
          settings.captain_ai_override_reason || '';
        this.ailabAiOverrideSetAt = settings.captain_ai_override_set_at || '';
        this.ailabAiOverrideSetByUserId =
          settings.captain_ai_override_set_by_user_id || '';
        this.selectedPortalSlug = this.inbox.help_center
          ? this.inbox.help_center.slug
          : '';
      });
    },
    async updateInbox() {
      try {
        const nextOverrideMode = this.ailabAiOverrideMode || 'inherit';
        const payload = {
          id: this.currentInboxId,
          name: this.selectedInboxName,
          enable_email_collect: this.emailCollectEnabled,
          allow_messages_after_resolved: this.allowMessagesAfterResolved,
          greeting_enabled: this.greetingEnabled,
          greeting_message: this.greetingMessage || '',
          greeting_config: this.greetingConfig,
          captain_ai_settings: {
            captain_ai_override_mode: nextOverrideMode,
            captain_ai_override_reason:
              nextOverrideMode === 'inherit'
                ? ''
                : this.ailabAiOverrideReason || '',
          },
          portal_id: this.selectedPortalSlug
            ? this.portals.find(
                portal => portal.slug === this.selectedPortalSlug
              ).id
            : null,
          lock_to_single_conversation: this.locktoSingleConversation,
          sender_name_type: this.senderNameType,
          business_name: this.businessName || null,
          channel: {
            widget_color: this.inbox.widget_color,
            website_url: this.channelWebsiteUrl,
            webhook_url: this.webhookUrl,
            welcome_title: this.channelWelcomeTitle || '',
            welcome_tagline: this.channelWelcomeTagline || '',
            selectedFeatureFlags: this.selectedFeatureFlags,
            reply_time: this.replyTime || 'in_a_few_minutes',
            continuity_via_email: this.continuityViaEmail,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async advanceAilabAiOverrideOneStep() {
      try {
        if (!this.canAdvanceAilabAiOverride) {
          return;
        }
        const nextMode = this.nextAilabAiOverrideMode;
        const reason =
          nextMode === 'inherit'
            ? ''
            : (this.ailabAiOverrideReason || 'rollout-next-ui').toString();

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.currentInboxId,
          captain_ai_settings: {
            captain_ai_override_mode: nextMode,
            captain_ai_override_reason: reason,
          },
        });

        // Keep local state in sync immediately (then refresh from server for audit fields).
        this.ailabAiOverrideMode = nextMode;
        this.ailabAiOverrideReason = reason;

        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.fetchInboxSettings();
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxAvatar',
          this.currentInboxId
        );
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(this.$t('INBOX_MGMT.DELETE.API.AVATAR_SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('INBOX_MGMT.DELETE.API.AVATAR_ERROR_MESSAGE')
        );
      }
    },
    toggleSenderNameType(key) {
      this.senderNameType = key;
    },
    onClickShowBusinessNameInput() {
      this.showBusinessNameInput = !this.showBusinessNameInput;
      if (this.showBusinessNameInput) {
        this.$nextTick(() => {
          this.$refs.businessNameInput.focus();
        });
      }
    },
  },
  validations: {
    webhookUrl: {
      shouldBeUrl,
    },
    selectedInboxName: {},
  },
};
</script>

<template>
  <div
    class="inbox-settings-page flex flex-col w-full h-full overflow-hidden !p-0 bg-white"
  >
    <!-- Header -->
    <header
      class="inbox-settings-header flex items-center justify-between flex-shrink-0 bg-white"
      style="height: 50px; border-bottom: 1px solid #eeeeef"
    >
      <div class="flex items-center gap-3" style="padding-left: 21px">
        <button
          type="button"
          class="back-icon-btn"
          aria-label="Back to inboxes"
          @click="goBack"
        >
          <span class="i-ph-caret-left-bold size-4" />
        </button>
        <div class="inbox-avatar-sm">
          <Avatar
            v-if="avatarUrl"
            :src="avatarUrl"
            :name="inbox.name"
            :size="28"
            rounded-full
          />
          <ChannelIcon v-else class="size-4 text-slate-700" :inbox="inbox" />
        </div>
        <span
          class="text-base font-semibold text-slate-900 truncate max-w-[400px]"
          >{{ inboxName }}</span>
      </div>
    </header>

    <!-- Tab content — full width (navigation is in the main sidebar) -->
    <!-- Two-column full-height pages need their own flex column context -->
    <div
      class="flex flex-col flex-1 min-h-0"
      :class="
        [
          'eyeCatcher',
          'bubbleLauncher',
          'poweredBy',
          'welcomeScreen',
          'widgetBuilder',
          'language',
          'greetingCard',
          'preChatForm',
          'ticketForm',
          'postChatForm',
        ].includes(selectedTabKey)
          ? 'overflow-hidden'
          : 'overflow-y-auto'
      "
    >
      <!-- Alerts -->
      <div
        v-if="
          microsoftUnauthorized ||
          facebookUnauthorized ||
          googleUnauthorized ||
          instagramUnauthorized ||
          whatsappUnauthorized ||
          hasDuplicateInstagramInbox
        "
        class="py-3 border-b border-slate-200 bg-white"
      >
        <MicrosoftReauthorize v-if="microsoftUnauthorized" :inbox="inbox" />
        <FacebookReauthorize v-if="facebookUnauthorized" :inbox="inbox" />
        <GoogleReauthorize v-if="googleUnauthorized" :inbox="inbox" />
        <InstagramReauthorize v-if="instagramUnauthorized" :inbox="inbox" />
        <WhatsappReauthorize v-if="whatsappUnauthorized" :inbox="inbox" />
        <DuplicateInboxBanner
          v-if="hasDuplicateInstagramInbox"
          :content="$t('INBOX_MGMT.ADD.INSTAGRAM.DUPLICATE_INBOX_BANNER')"
        />
      </div>

      <!-- Settings Tab -->
      <div v-if="selectedTabKey === 'inbox_settings'" class="is-page">
        <div class="is-body">
          <!-- ── Page header ── -->
          <div class="is-hd">
            <h2 class="is-page-title">Inbox settings</h2>
            <p class="is-page-desc">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_UPDATE_SUB_TEXT') }}
            </p>
          </div>

          <!-- ── Form section ── -->
          <section class="is-section"><div class="is-section-inner">
            <div class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_AVATAR.LABEL')
                }}</label>
              </div>
              <div class="form-control-col">
                <Avatar
                  :src="avatarUrl"
                  :size="64"
                  :icon-name="inboxIcon"
                  name=""
                  allow-upload
                  rounded-full
                  @upload="handleImageUpload"
                  @delete="handleAvatarDelete"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{ inboxNameLabel }}</label>
              </div>
              <div class="form-control-col">
                <input
                  v-model="selectedInboxName"
                  type="text"
                  class="form-input"
                  :placeholder="inboxNamePlaceHolder"
                />
              </div>
            </div>

            <div v-if="isAPIInbox" class="form-row">
              <div class="form-label-col">
                <label class="form-label">API Provider</label>
              </div>
              <div class="form-control-col">
                <input
                  v-model="webhookUrl"
                  type="text"
                  class="form-input"
                  :placeholder="$t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.PLACEHOLDER')"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL'
                  )
                }}</label>
                <p class="form-hint">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
                    )
                  }}
                </p>
              </div>
              <div class="form-control-col">
                <select v-model="greetingEnabled" class="form-select">
                  <option :value="true">
                    {{
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.ENABLED'
                      )
                    }}
                  </option>
                  <option :value="false">
                    {{
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.DISABLED'
                      )
                    }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="greetingEnabled" class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                  )
                }}</label>
              </div>
              <div class="form-control-col">
                <GreetingsEditor
                  v-model="greetingMessage"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                    )
                  "
                  :richtext="!textAreaChannels"
                />
              </div>
            </div>

            <div v-if="!isAVoiceChannel" class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t('INBOX_MGMT.HELP_CENTER.LABEL')
                }}</label>
                <p class="form-hint">
                  {{ $t('INBOX_MGMT.HELP_CENTER.SUB_TEXT') }}
                </p>
              </div>
              <div class="form-control-col">
                <select v-model="selectedPortalSlug" class="form-select">
                  <option value="">
                    {{ $t('INBOX_MGMT.HELP_CENTER.PLACEHOLDER') }}
                  </option>
                  <option v-for="p in portals" :key="p.slug" :value="p.slug">
                    {{ p.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.LABEL')
                }}</label>
                <p class="form-hint">
                  {{ $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.HELP_TEXT') }}
                </p>
              </div>
              <div class="form-control-col">
                <select v-model="ailabAiOverrideMode" class="form-select">
                  <option value="inherit">
                    {{
                      $t(
                        'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.OPTIONS.INHERIT'
                      )
                    }}
                  </option>
                  <option value="auto_suggest">
                    {{
                      $t(
                        'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.OPTIONS.AUTO_SUGGEST'
                      )
                    }}
                  </option>
                  <option value="suggest">
                    {{
                      $t(
                        'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.MODE.OPTIONS.SUGGEST'
                      )
                    }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.ADVANCE.LABEL')
                }}</label>
                <p class="form-hint">
                  {{
                    $t(
                      'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.ADVANCE.HELP_TEXT',
                      { next: nextAilabAiOverrideModeLabel }
                    )
                  }}
                </p>
              </div>
              <div class="form-control-col">
                <button
                  class="secondary-action-btn"
                  :disabled="!canAdvanceAilabAiOverride"
                  @click="advanceAilabAiOverrideOneStep"
                >
                  {{
                    $t(
                      'INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.ADVANCE.BUTTON_TEXT'
                    )
                  }}
                </button>
              </div>
            </div>

            <div class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.REASON.LABEL')
                }}</label>
                <p class="form-hint">
                  {{
                    $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.REASON.HELP_TEXT')
                  }}
                </p>
              </div>
              <div class="form-control-col">
                <input
                  v-model="ailabAiOverrideReason"
                  type="text"
                  class="form-input"
                  :disabled="isAilabAiOverrideInherit"
                  :placeholder="
                    $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.REASON.PLACEHOLDER')
                  "
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-label-col">
                <label class="form-label">{{
                  $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.AUDIT.LABEL')
                }}</label>
                <p class="form-hint">
                  {{
                    $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.AUDIT.HELP_TEXT')
                  }}
                </p>
              </div>
              <div class="form-control-col">
                <div class="form-hint">
                  <span v-if="ailabAiOverrideSetAt">
                    {{
                      $t('INBOX_MGMT.EDIT.AILAB_AI_OVERRIDE.AUDIT.VALUE', {
                        at: ailabAiOverrideSetAt,
                        by: ailabAiOverrideSetByUserId || '-',
                      })
                    }}
                  </span>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
          </div></section>

          <!-- ── Footer ── -->
          <div class="is-footer">
            <button
              class="is-save-btn"
              :disabled="v$.$invalid"
              @click="updateInbox"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Other Tabs -->
      <div v-if="selectedTabKey === 'collaborators'" class="tab-content-wrap">
        <CollaboratorsPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'configuration'" class="tab-content-wrap">
        <ConfigurationPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'csat'" class="tab-content-wrap">
        <CustomerSatisfactionPage :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'preChatForm'" class="tab-content-fullpage">
        <PreChatFormSettings :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'ticketForm'" class="tab-content-fullpage">
        <TicketFormSettings :inbox="inbox" />
      </div>
      <div
        v-if="selectedTabKey === 'postChatForm'"
        class="tab-content-fullpage"
      >
        <PostChatFormSettings :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'businesshours'" class="tab-content-wrap">
        <WeeklyAvailability :inbox="inbox" />
      </div>
      <div
        v-if="selectedTabKey === 'widgetBuilder'"
        class="tab-content-fullpage"
      >
        <WidgetBuilder :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'eyeCatcher'" class="tab-content-fullpage">
        <EyeCatcherSettings :inbox="inbox" />
      </div>
      <div
        v-if="selectedTabKey === 'bubbleLauncher'"
        class="tab-content-fullpage"
      >
        <BubbleLauncherSettings :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'poweredBy'" class="tab-content-fullpage">
        <PoweredBySettings :inbox="inbox" />
      </div>
      <div
        v-if="selectedTabKey === 'welcomeScreen'"
        class="tab-content-fullpage"
      >
        <WelcomeScreen :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'language'" class="tab-content-fullpage">
        <LanguageSettings :inbox="inbox" />
      </div>
      <div
        v-if="selectedTabKey === 'greetingCard'"
        class="tab-content-fullpage"
      >
        <GreetingCardSettings :inbox="inbox" />
      </div>
      <div v-if="selectedTabKey === 'availability'" class="tab-content-wrap">
        <AvailabilitySettings
          :inbox="inbox"
          @go-to-ticket-form="goToLanguageSection('away')"
          @go-to-queue="goToLanguageSection('queue')"
        />
      </div>
      <div
        v-if="selectedTabKey === 'inactivityTimeouts'"
        class="tab-content-wrap"
      >
        <InactivityTimeouts :inbox="inbox" />
      </div>
      <div
        v-if="selectedTabKey === 'botConfiguration'"
        class="tab-content-wrap"
      >
        <BotConfiguration :inbox="inbox" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.inbox-settings-page {
  /* Force “latest design” white surface for both light/dark modes */
  --wlab-bg-primary: #ffffff;
  --wlab-bg-tertiary: #ffffff;
  --wlab-bg-hover: #f3f4f6;
  --wlab-bg-input: #ffffff;
  --wlab-border-default: #e5e7eb;
  --wlab-border-light: #eef2f7;
  --wlab-text-primary: #0f172a;
  --wlab-text-secondary: #1f2937;
  --wlab-text-tertiary: #475569;
  --wlab-text-muted: #64748b;

  background-color: #ffffff;
  color: var(--wlab-text-primary);

  /* Scrollbar: thin, no arrows/track background */
  scrollbar-color: auto transparent;
  scrollbar-width: thin;
}

.inbox-settings-header {
  background-color: #ffffff;
}

.back-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--wlab-border-default);
  background: #ffffff;
  color: var(--wlab-text-secondary);
  cursor: pointer;
  transition:
    background-color 150ms ease,
    border-color 150ms ease;
  flex-shrink: 0;
}

.back-icon-btn:hover {
  background: var(--wlab-bg-hover);
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-tertiary);
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.back-btn:hover {
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
}

.inbox-avatar-sm {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--wlab-bg-hover);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.inbox-settings-page::-webkit-scrollbar {
  width: 10px;
  height: 10px;
  background: transparent;
}

.inbox-settings-page::-webkit-scrollbar-track {
  background: transparent;
}

.inbox-settings-page::-webkit-scrollbar-corner {
  background: transparent;
}

.inbox-settings-page::-webkit-scrollbar-button {
  width: 0;
  height: 0;
  display: none;
  background: transparent;
}

.lc-sidebar {
  width: 264px;
  flex: 0 0 264px;
  background: #fbfcfe;
  border-right: 1px solid var(--wlab-border-default);
  overflow-y: auto;
  padding: 20px 14px;
}

.lc-sidebar__inner {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.lc-sidebar__group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 14px;
  border-bottom: 1px solid #edf1f7;
}

.lc-sidebar__group:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.lc-sidebar__group-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  color: var(--wlab-text-primary);
  padding: 0 10px 8px;
  line-height: 1.35;
}

.lc-nav-item {
  width: 100%;
  text-align: left;
  position: relative;
  border: 1px solid transparent;
  background: transparent;
  color: var(--wlab-text-tertiary);
  font-size: 12px;
  line-height: 1.4;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    border-color 150ms ease,
    color 150ms ease;
}

.lc-nav-item:hover {
  background: #f1f5f9;
  color: var(--wlab-text-primary);
}

.lc-nav-item.active {
  background: #eaf2ff;
  border-color: #c9dbff;
  color: #0b3f9d;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #2563eb;
}

.tabs-row {
  background: var(--wlab-bg-primary);
}

.tabs-container {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 8px 0;
}

.tab-btn {
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-muted);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.tab-btn:hover {
  color: var(--wlab-text-secondary);
  background: var(--wlab-bg-tertiary);
}

.tab-btn.active {
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-hover);
  border: 1px solid var(--wlab-border-default);
  box-shadow: 0 2px 0 0 rgba(255, 255, 255, 0.3);
}

.settings-content {
  display: flex;
  flex-direction: column;
}

.settings-section {
  border-bottom: 1px solid var(--wlab-border-light);
}

.section-header {
  padding: 20px 24px;
  background: var(--wlab-bg-tertiary);
  border-bottom: 1px solid var(--wlab-border-light);
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--wlab-text-primary);
  margin: 0 0 4px;
}

.section-desc {
  font-size: 11px;
  color: var(--wlab-text-muted);
  margin: 0;
}

.section-body {
  padding: 0;
}

.section-footer {
  padding: 16px 24px;
  background: var(--wlab-bg-tertiary);
  border-top: 1px solid var(--wlab-border-light);
}

.form-row {
  display: flex;
  padding: 20px 24px;
  border-bottom: 1px solid var(--wlab-border-light);
  gap: 24px;
}

.form-row:last-child {
  border-bottom: none;
}

.form-label-col {
  width: 280px;
  flex-shrink: 0;
}

.form-control-col {
  flex: 1;
  min-width: 0;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--wlab-text-primary);
  margin: 0;
}

.form-hint {
  font-size: 11px;
  color: var(--wlab-text-muted);
  margin: 6px 0 0;
  line-height: 1.5;
}

.form-input,
.form-select {
  width: 100%;
  max-width: 400px;
  padding: 10px 14px;
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 8px;
  font-size: 13px;
  color: var(--wlab-text-primary);
  outline: none;
  transition: all 0.15s ease;
}

.form-input:focus,
.form-select:focus {
  border-color: var(--wlab-text-muted);
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  font-family:
    'Montserrat',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  background-color: #fff;
  color: #000;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.primary-action-btn:hover {
  background-color: #f0f0f0;
}

.primary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.secondary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 13px;
  line-height: 16px;
  font-weight: 600;
  font-family:
    'Montserrat',
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  background-color: transparent !important;
  color: var(--wlab-text-primary) !important;
  border: 1px solid var(--wlab-border-default) !important;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.secondary-action-btn:hover {
  background-color: var(--wlab-bg-hover) !important;
}

.secondary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tab-content-wrap {
  padding: 0 24px;
}

/* Full-height two-panel pages — uses flex growth, not height: 100%, to avoid flex chain issues */
.tab-content-fullpage {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── Inbox Settings: unified page layout (mirrors Availability/Collaborators) ── */
.is-page {
  background: #ffffff;
}

.is-body {
  max-width: 720px;
  margin: 0;
  padding: 32px 32px 80px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.is-hd {
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
}

.is-page-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}

.is-page-desc {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  line-height: 1.5;
}

.is-section {
  display: flex;
  flex-direction: column;
}

.is-section-inner {
  display: flex;
  flex-direction: column;
}

.is-footer {
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  margin-top: 4px;
}

.is-save-btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 20px;
  border-radius: 8px;
  background: #111827;
  color: #ffffff;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.is-save-btn:hover:not(:disabled) {
  background: #1f2937;
}

.is-save-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Force light appearance for all form elements (global _base.scss uses
   long :not() chains with specificity 0,14,1 — needs !important)          ── */
.is-section :deep(input[type='text']),
.is-section :deep(input[type='url']),
.is-section :deep(input[type='number']),
.is-section :deep(input[type='email']),
.is-section :deep(input[type='tel']) {
  background-color: #ffffff !important;
  color: #111827 !important;
  -webkit-text-fill-color: #111827 !important;
  border-color: #e5e7eb !important;
  outline-color: #4384f5 !important;
}

.is-section :deep(select) {
  background-color: #ffffff !important;
  color: #111827 !important;
  -webkit-text-fill-color: #111827 !important;
  border-color: #e5e7eb !important;
}

.is-section :deep(select option) {
  background-color: #ffffff !important;
  color: #111827 !important;
}

.is-section :deep(label) {
  color: #111827 !important;
}

.is-section :deep(.form-hint) {
  color: #6b7280 !important;
}

.is-section :deep(textarea) {
  background-color: #ffffff !important;
  color: #111827 !important;
  -webkit-text-fill-color: #111827 !important;
  border-color: #e5e7eb !important;
}
</style>
